import { useEffect, useRef } from 'react';

import {
  ME_ACTIVITY_WRITE_THROTTLE_MS,
  ME_IDLE_CHECK_INTERVAL_MS,
  clearLastActivity,
  isMeSessionExpired,
  readLastActivity,
  writeLastActivity,
} from '@/components/me/mePortal';
import { logger } from '@/lib/sentry';

import { isMeSigningOut, useMeSignOut } from './useMeSignOut';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;

// Capture phase: `scroll` does not bubble, so a scroll inside a dialog or any
// other scroll container only reaches a window listener while capturing.
const LISTEN = { capture: true, passive: true } as const;
const UNLISTEN = { capture: true } as const;

/**
 * Signs the portal out after 30 minutes without interaction (#1768).
 *
 * Activity updates an in-memory timestamp on every event and the
 * `me-last-activity` marker at most once per throttle window; the newest
 * activity is flushed to the marker when the tab is hidden or the page is left,
 * so the boot check of the next load sees it. The check runs on an interval
 * AND immediately when the tab becomes visible or gains focus, because phones
 * suspend timers in the background. Another tab's activity counts through the
 * shared marker (see useMeActivityRelay). On unmount the marker is removed only
 * while it still holds this tab's own last write: a newer value belongs to a
 * live /me tab elsewhere and must survive.
 */
export function useMeIdleSignOut(): void {
  const { signOut } = useMeSignOut();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  useEffect(() => {
    // Mounting is activity: the boot gate has already handled a stale marker.
    let lastActivity = Date.now();
    let lastWrite = lastActivity;
    writeLastActivity(lastActivity);

    const write = (at: number) => {
      lastWrite = at;
      writeLastActivity(at);
    };

    const onActivity = () => {
      if (isMeSigningOut()) return;
      const now = Date.now();
      lastActivity = now;
      if (now - lastWrite >= ME_ACTIVITY_WRITE_THROTTLE_MS) write(now);
    };

    const flush = () => {
      if (!isMeSigningOut() && lastActivity > lastWrite) write(lastActivity);
    };

    const check = () => {
      const last = Math.max(lastActivity, readLastActivity() ?? 0);
      if (isMeSessionExpired(last, Date.now())) {
        signOutRef.current('idle').catch((error: unknown) => {
          logger.error('me-portal: idle sign-out failed', { error });
        });
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
      else flush();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, LISTEN);
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flush);
    window.addEventListener('focus', check);
    const interval = window.setInterval(check, ME_IDLE_CHECK_INTERVAL_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity, UNLISTEN);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('focus', check);
      window.clearInterval(interval);
      if (readLastActivity() === lastWrite) clearLastActivity();
    };
  }, []);
}

/**
 * Mounted in the admin AppLayout (#1768). Clerk's sign-out is session-wide, so
 * an idle /me tab would also sign out a user who is busy in an admin tab of the
 * same browser. While a live /me marker exists, admin activity refreshes it with
 * the same throttle. It never creates a marker, and never revives an expired
 * one: that is the evidence the boot check of the next load acts on.
 */
export function useMeActivityRelay(): void {
  useEffect(() => {
    // Throttles the storage read as well as the write.
    let lastSeen = 0;

    const onActivity = () => {
      const now = Date.now();
      if (now - lastSeen < ME_ACTIVITY_WRITE_THROTTLE_MS) return;
      lastSeen = now;
      const marker = readLastActivity();
      if (marker === null || isMeSessionExpired(marker, now)) return;
      writeLastActivity(now);
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, LISTEN);
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity, UNLISTEN);
      }
    };
  }, []);
}
